import React from 'react';
import { Paper, Box, Typography, Skeleton, useTheme } from '@mui/material';

interface ChartCardProps {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  height?: number;
  loading?: boolean;
  empty?: boolean;
  emptyMessage?: string;
  children: React.ReactNode;
}

/**
 * Elegant container for every finance chart. Consistent padding, border,
 * loading skeleton, and empty state — so individual charts only concern
 * themselves with the recharts markup.
 */
const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  right,
  height = 300,
  loading,
  empty,
  emptyMessage = 'No data for this window',
  children,
}) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {right && <Box sx={{ flexShrink: 0 }}>{right}</Box>}
      </Box>

      <Box sx={{ flex: 1, minHeight: height, position: 'relative' }}>
        {loading ? (
          <Skeleton variant="rounded" height={height} sx={{ borderRadius: 1.5 }} />
        ) : empty ? (
          <Box
            sx={{
              height,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.disabled',
            }}
          >
            <Typography variant="body2">{emptyMessage}</Typography>
          </Box>
        ) : (
          children
        )}
      </Box>
    </Paper>
  );
};

export default ChartCard;
